import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Mail, Megaphone, Wrench, Crosshair, Youtube, Monitor, Tv, Wifi, ArrowUpRight, Download, AtSign, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import PixelField from "./PixelField";

const GUIDE_LINKS = [
  {
    label: "YouTube Walkthrough",
    sub: "Watch the full setup video",
    icon: Youtube,
    href: "https://www.youtube.com/watch?v=xAe-J43vlRI",
    external: true,
    testid: "guide-youtube-button",
  },
  {
    label: "Screen Setup",
    sub: "Install and power up your display",
    icon: Monitor,
    lightbox: {
      title: '24" Portrait Tabletop Screen Setup Guide',
      src: "/screen-setup-guide.png",
      download: "dolphin-screen-setup-guide.png",
      alt: "Screen setup guide — what's in the box, attach the mount, connect and power on",
    },
    testid: "guide-screen-setup-button",
  },
  {
    label: "Stick Setup",
    sub: "Configure your Dolphin stick",
    icon: Tv,
    lightbox: {
      title: "Dolphin Stick Setup",
      src: "/stick-setup-guide.png",
      download: "dolphin-stick-setup-guide.png",
      alt: "Dolphin Stick setup guide — what's in the box, power up, connect to stick, connect to display",
    },
    testid: "guide-stick-setup-button",
  },
  {
    label: "WiFi Setup",
    sub: "Connect your device to WiFi",
    icon: Wifi,
    lightbox: {
      title: "Dolphin Stick — Wi-Fi Setup Guide",
      src: "/wifi-setup-guide.png",
      download: "dolphin-wifi-setup-guide.png",
      alt: "Wi-Fi setup guide — quit the app, enter access code 9977, open settings, connect to Wi-Fi, relaunch",
    },
    testid: "guide-wifi-setup-button",
  },
];

const CARDS = [
  {
    title: "Contact Us",
    description:
      "Have a question or want to learn more? Get in touch with our team to explore how Dolphin can support your business.",
    icon: Mail,
    testid: "feature-card-contact-us",
    video: "/contact-video.mp4",
    contact: true,
  },
  {
    title: "Campaign Management",
    description:
      "Launch, manage, and optimize your advertising campaigns with easy-to-use tools and dedicated support, all from one platform.",
    icon: Megaphone,
    testid: "feature-card-campaign-management",
    video: "/campaign-video.mp4",
    href: "https://manage.dolphin-digital.com/login",
  },
  {
    title: "Installation Guide",
    description:
      "Get step-by-step guidance to quickly install, set up, and start using your Dolphin Digital equipment.",
    icon: Wrench,
    testid: "feature-card-installation-guide",
    video: "/install-video.mp4",
    guide: true,
    image:
      "https://images.unsplash.com/photo-1768176136613-96a7bfc0049e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxyZXRhaWwlMjBhaXNsZSUyMGZ1dHVyaXN0aWMlMjBsaWdodGluZ3xlbnwwfHx8fDE3ODY1NjU1NDJ8MA&ixlib=rb-4.1.0&q=85",
  },
];

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function SupportSection() {
  const ref = useRef(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideImage, setGuideImage] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    const open = () => setContactOpen(true);
    window.addEventListener("open-contact-modal", open);
    return () => window.removeEventListener("open-contact-modal", open);
  }, []);

  const anyModalOpen = guideOpen || !!guideImage || contactOpen;
  useEffect(() => {
    if (!window.__lenis) return;
    if (anyModalOpen) window.__lenis.stop();
    else window.__lenis.start();
  }, [anyModalOpen]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section
      ref={ref}
      id="support"
      data-testid="support-section"
      className="relative overflow-hidden pb-24 pt-36 md:pb-32 md:pt-44"
    >
      {/* ambient background */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <motion.div
          style={{ y: gridY }}
          className="absolute inset-x-0 top-0 h-full opacity-[0.35]"
        >
          <div
            className="h-full w-full"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
              backgroundSize: "72px 72px",
              maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 30%, transparent 75%)",
              WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 30%, transparent 75%)",
            }}
          />
        </motion.div>
      </div>
      <PixelField />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col items-center text-center">
          <motion.span
            data-testid="ai-technology-badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] py-1.5 pl-1.5 pr-4 backdrop-blur-sm"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#3ea6ff] to-[#e0359e]">
              <Crosshair className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-medium text-white/80">Support &amp; Resources</span>
          </motion.span>

          <h1
            data-testid="support-headline"
            className="relative mt-8 font-display text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.25}>Support &amp; Resources</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-gradient">Everything You Need to Keep Your Network Running.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="support-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg"
          >
            Access helpful resources, campaign support, installation guidance, and direct
            assistance from the Dolphin team &mdash; all in one place.
          </motion.p>
        </div>

        <div
          id="technology"
          data-testid="feature-cards-grid"
          className="mt-16 grid gap-6 sm:grid-cols-2 md:mt-20 lg:grid-cols-3 lg:gap-8"
        >
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            const Tag = card.href ? motion.a : motion.article;
            return (
              <Tag
                key={card.title}
                data-testid={card.testid}
                {...(card.href ? { href: card.href, target: "_blank", rel: "noopener noreferrer" } : {})}
                {...(card.guide
                  ? {
                      onClick: () => setGuideOpen(true),
                      role: "button",
                      tabIndex: 0,
                      "aria-haspopup": "dialog",
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setGuideOpen(true);
                        }
                      },
                    }
                  : {})}
                {...(card.contact
                  ? {
                      onClick: () => setContactOpen(true),
                      role: "button",
                      tabIndex: 0,
                      "aria-haspopup": "dialog",
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setContactOpen(true);
                        }
                      },
                    }
                  : {})}
                initial={{ opacity: 0, y: 48 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.14, ease: EASE }}
                whileHover={{ y: -8 }}
                className={`group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm transition-[border-color,box-shadow] duration-500 hover:border-[#e0359e]/30 hover:shadow-[0_0_40px_rgba(224,53,158,0.15),0_0_30px_rgba(62,166,255,0.12)]`}
              >
                <div className="relative h-48 overflow-hidden border-b border-white/10">
                  {card.video ? (
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      data-testid={`${card.testid}-video`}
                      className="h-full w-full object-cover opacity-90 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-100"
                    >
                      <source src={card.video.replace(".mp4", ".webm")} type="video/webm" />
                      <source src={card.video} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={card.image}
                      alt={card.title}
                      loading="lazy"
                      className="h-full w-full object-cover mix-blend-luminosity opacity-80 transition-[transform,opacity,mix-blend-mode] duration-700 group-hover:scale-105 group-hover:opacity-100 group-hover:mix-blend-normal"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 to-transparent" />
                </div>
                <div className="flex flex-col gap-3 p-6 text-left">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#3ea6ff] to-[#e0359e] text-white shadow-[0_6px_18px_-6px_rgba(194,29,142,0.5)]">
                      <Icon className="h-[18px] w-[18px]" />
                    </span>
                    <h3 className="font-display text-lg font-semibold text-white md:text-xl">
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-white/50">{card.description}</p>
                </div>
              </Tag>
            );
          })}
        </div>
      </div>

      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent
          data-testid="installation-guide-modal"
          data-lenis-prevent
          className="border-white/10 bg-[#0d0d12] text-white sm:rounded-2xl"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-semibold text-white">
              Installation Guide
            </DialogTitle>
            <DialogDescription className="text-sm text-white/50">
              Choose a guide to get your Dolphin Digital equipment up and running.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex flex-col gap-3">
            {GUIDE_LINKS.map((item) => {
              const GIcon = item.icon;
              const rowClass =
                "group flex w-full items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left transition-[border-color,background-color] duration-300 hover:border-[#e0359e]/30 hover:bg-white/[0.06]";
              const inner = (
                <>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#3ea6ff] to-[#e0359e] text-white">
                    <GIcon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex flex-col text-left">
                    <span className="text-sm font-semibold text-white">{item.label}</span>
                    <span className="text-xs text-white/50">{item.sub}</span>
                  </span>
                  <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-white/40 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
                </>
              );
              if (item.lightbox) {
                return (
                  <button
                    key={item.label}
                    type="button"
                    data-testid={item.testid}
                    onClick={() => {
                      setGuideOpen(false);
                      setGuideImage(item.lightbox);
                    }}
                    className={rowClass}
                  >
                    {inner}
                  </button>
                );
              }
              return (
                <a
                  key={item.label}
                  href={item.href}
                  data-testid={item.testid}
                  {...(item.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : { onClick: (e) => e.preventDefault() })}
                  className={rowClass}
                >
                  {inner}
                </a>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!guideImage} onOpenChange={(open) => !open && setGuideImage(null)}>
        <DialogContent
          data-testid="guide-image-lightbox"
          data-lenis-prevent
          className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto border-white/10 bg-[#0d0d12] p-4 text-white sm:rounded-2xl"
        >
          {guideImage && (
            <>
              <DialogHeader className="px-1 pb-1">
                <DialogTitle className="font-display text-lg font-semibold text-white">
                  {guideImage.title}
                </DialogTitle>
              </DialogHeader>
              <img
                src={guideImage.src}
                alt={guideImage.alt}
                data-testid="guide-lightbox-image"
                className="w-full rounded-xl border border-white/10"
              />
              <a
                href={guideImage.src}
                download={guideImage.download}
                data-testid="guide-download-button"
                className="btn-primary mx-auto mt-1 px-6 py-3 text-sm"
              >
                <Download className="h-4 w-4" />
                Download Guide
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent
          data-testid="contact-us-modal"
          data-lenis-prevent
          className="overflow-hidden border-white/10 bg-[#0d0d12] text-white sm:rounded-2xl"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 left-1/2 h-48 w-72 -translate-x-1/2 rounded-full bg-[#e0359e]/20 blur-[80px]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-16 top-10 h-40 w-40 rounded-full bg-[#3ea6ff]/15 blur-[70px]"
          />
          <DialogHeader className="relative">
            <DialogTitle className="font-display text-xl font-semibold text-white">
              Contact Us
            </DialogTitle>
            <DialogDescription className="text-sm text-white/50">
              Reach the Dolphin team directly &mdash; we usually reply within one business day.
            </DialogDescription>
          </DialogHeader>
          <div className="relative mt-2 flex flex-col gap-3">
            {[
              {
                label: "General enquiries",
                value: "info@dolphin-digital.com",
                href: "mailto:info@dolphin-digital.com",
                icon: Mail,
                testid: "contact-email-info-link",
              },
              {
                label: "Campaign & ads support",
                value: "dolphintxi.ads@dolphin-digital.com",
                href: "mailto:dolphintxi.ads@dolphin-digital.com",
                icon: AtSign,
                testid: "contact-email-ads-link",
              },
              {
                label: "Phone",
                value: "+1 845-244-5803",
                href: "tel:+18452445803",
                icon: Phone,
                testid: "contact-phone-link",
              },
            ].map((item) => {
              const CIcon = item.icon;
              return (
                <a
                  key={item.value}
                  href={item.href}
                  data-testid={item.testid}
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-[border-color,background-color,box-shadow] duration-300 hover:border-[#3ea6ff]/40 hover:bg-white/[0.06] hover:shadow-[0_0_24px_rgba(62,166,255,0.12)]"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#3ea6ff] to-[#e0359e] text-white">
                    <CIcon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex min-w-0 flex-col text-left">
                    <span className="text-xs uppercase tracking-wider text-white/40">
                      {item.label}
                    </span>
                    <span className="truncate text-sm font-semibold text-white">{item.value}</span>
                  </span>
                  <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-white/40 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
                </a>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
